import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from "@mui/material";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import { useRoutes, useFetchRouteById } from "../../../../../hooks/useRoutes";
import { useLocalizaciones } from "../../../../../hooks/useLocalizations";
import LocalizationForm from "../../../../administracion/localizaciones/LocalizationForm";

import RouteTemplateSelector from "./RouteTemplateSelector";
import SelectedStopsPanel from "./SelectedStopsPanel";
import AvailableLocationsPanel from "./AvailableLocationsPanel";

const setSameTimeWithDayOffset = (
    targetStartDateStr,
    sourceStartDateStr,
    sourceDateTimeStr
) => {
    if (!targetStartDateStr || !sourceStartDateStr || !sourceDateTimeStr) {
        return null;
    }

    const targetStart = new Date(`${targetStartDateStr}T00:00:00`);
    const sourceStart = new Date(`${sourceStartDateStr}T00:00:00`);
    const sourceDateTime = new Date(sourceDateTimeStr);

    if (
        Number.isNaN(targetStart.getTime()) ||
        Number.isNaN(sourceStart.getTime()) ||
        Number.isNaN(sourceDateTime.getTime())
    ) {
        return null;
    }

    const dayOffset = Math.floor(
        (sourceDateTime - sourceStart) / (24 * 60 * 60 * 1000)
    );

    const result = new Date(targetStart);
    result.setDate(result.getDate() + dayOffset);
    result.setHours(
        sourceDateTime.getHours(),
        sourceDateTime.getMinutes(),
        0,
        0
    );

    return result.toISOString();
};

const normalizeStop = (stop) => ({
    ...stop,
    locid: Number(stop.locid),
    locnom: stop.locnom || stop.LocNom || "Sin nombre",
    parhorest: stop.parhorest || stop.ParHorEst || null,
});

const ManifestAssignRouteForm = ({
    open,
    handleClose,
    manifest,
    updByID,
    onAssigned,
    defaultParadas = [],
}) => {
    const { routes = [] } = useRoutes();
    const { localizaciones = [] } = useLocalizaciones();

    const [selectedRouteId, setSelectedRouteId] = useState("");
    const [selectedStops, setSelectedStops] = useState([]);
    const [searchLocTerm, setSearchLocTerm] = useState("");
    const [openLocModal, setOpenLocModal] = useState(false);

    const { data: selectedRouteData, isFetching: isLoadingRoute } =
        useFetchRouteById(selectedRouteId, Boolean(selectedRouteId));

    useEffect(() => {
        if (!open) return;

        if (Array.isArray(defaultParadas) && defaultParadas.length > 0) {
            setSelectedStops(defaultParadas.map(normalizeStop));
            return;
        }

        setSelectedStops([]);
        setSelectedRouteId("");
        setSearchLocTerm("");
    }, [open, defaultParadas]);

    useEffect(() => {
        if (!selectedRouteData?.paradas?.length) return;

        setSelectedStops(
            selectedRouteData.paradas
                .slice()
                .sort((a, b) => Number(a.parord || 0) - Number(b.parord || 0))
                .map(normalizeStop)
        );
    }, [selectedRouteData]);

    const availableStops = useMemo(() => {
        const selectedIds = new Set(
            selectedStops.map((stop) => Number(stop.locid))
        );

        return (localizaciones || [])
            .map(normalizeStop)
            .filter((loc) => !selectedIds.has(Number(loc.locid)));
    }, [localizaciones, selectedStops]);

    const canSave = selectedStops.length >= 2;
    const hasStopsWithoutTime = selectedStops.some((stop) => !stop.parhorest);

    const handleApplyTemplate = (template) => {
        if (!template?.paradas?.length) return;

        if (template.rutid) {
            setSelectedRouteId(String(template.rutid));
        }

        const targetStart = manifest?.manfec;
        const sourceStart = template?.manfec;

        const adjustedStops = template.paradas
            .slice()
            .sort((a, b) => Number(a.parord || 0) - Number(b.parord || 0))
            .map((stop) => {
                const normalized = normalizeStop(stop);

                const parhorest =
                    sourceStart && targetStart && normalized.parhorest
                        ? setSameTimeWithDayOffset(
                            targetStart,
                            sourceStart,
                            normalized.parhorest
                        )
                        : normalized.parhorest;

                return {
                    ...normalized,
                    parhorest,
                };
            });

        setSelectedStops(adjustedStops);
    };

    const handleAddStop = (loc) => {
        setSelectedStops((prev) => [
            ...prev,
            {
                ...normalizeStop(loc),
                parhorest: null,
            },
        ]);
    };

    const handleRemoveStop = (stop) => {
        setSelectedStops((prev) =>
            prev.filter((item) => Number(item.locid) !== Number(stop.locid))
        );
    };

    const handleTimeChange = (locid, newTime) => {
        setSelectedStops((prev) =>
            prev.map((stop) =>
                Number(stop.locid) === Number(locid)
                    ? {
                        ...stop,
                        parhorest: newTime,
                    }
                    : stop
            )
        );
    };

    const handleReorderStops = (nextStops) => {
        setSelectedStops(nextStops);
    };

    const handleCreateLocation = (newLoc) => {
        const tempLoc = {
            ...newLoc,
            locid: newLoc.locid || Date.now(),
            parhorest: null,
        };

        setSelectedStops((prev) => [...prev, normalizeStop(tempLoc)]);
        setOpenLocModal(false);
    };

    const handleSubmit = () => {
        if (!canSave) return;

        const paradas = selectedStops.map((stop, index, arr) => ({
            locid: stop.locid,
            parord: index + 1,
            partip:
                index === 0
                    ? "ORIGEN"
                    : index === arr.length - 1
                        ? "DESTINO"
                        : "INTERMEDIA",
            parhorest: stop.parhorest || null,
        }));

        onAssigned({
            rutid: selectedRouteId || null,
            updbyid: updByID,
            paradas,
        });
    };

    return (
        <>
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
                <DialogTitle>
                    <Typography variant="h6" fontWeight="bold">
                        Asignar ruta al manifiesto
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        MN-{String(manifest?.manid || "").padStart(6, "0")} ·{" "}
                        {manifest?.manfec || "-"}
                        {manifest?.manfecfin ? ` → ${manifest.manfecfin}` : ""}
                    </Typography>
                </DialogTitle>

                <DialogContent dividers>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <Stack spacing={2.5}>
                            <RouteTemplateSelector
                                open={open}
                                manifest={manifest}
                                onApplyTemplate={handleApplyTemplate}
                            />

                            <Divider />

                            <TextField
                                select
                                label="Ruta base"
                                value={selectedRouteId || ""}
                                onChange={(e) => setSelectedRouteId(e.target.value)}
                                fullWidth
                                size="small"
                                helperText={
                                    isLoadingRoute
                                        ? "Cargando paradas de la ruta..."
                                        : "Opcional. Puedes trabajar manualmente sin ruta base."
                                }
                            >
                                <MenuItem value="">Sin ruta / Manual</MenuItem>
                                {routes.map((route) => (
                                    <MenuItem key={route.rutid} value={route.rutid}>
                                        {route.rutnom}
                                    </MenuItem>
                                ))}
                            </TextField>

                            {!canSave && (
                                <Alert severity="info" variant="outlined">
                                    Debe seleccionar al menos 2 paradas para guardar la ruta.
                                </Alert>
                            )}

                            {canSave && hasStopsWithoutTime && (
                                <Alert severity="warning" variant="outlined">
                                    Existen paradas sin hora estimada. Puede guardar, pero no podrá
                                    pasar el manifiesto a PLANIFICADO hasta completar los horarios.
                                </Alert>
                            )}

                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <SelectedStopsPanel
                                        stops={selectedStops}
                                        onChange={handleReorderStops}
                                        onRemove={handleRemoveStop}
                                        onTimeChange={handleTimeChange}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <AvailableLocationsPanel
                                        locations={availableStops}
                                        searchTerm={searchLocTerm}
                                        setSearchTerm={setSearchLocTerm}
                                        onAdd={handleAddStop}
                                        onCreateLocation={() => setOpenLocModal(true)}
                                    />
                                </Grid>
                            </Grid>
                        </Stack>
                    </LocalizationProvider>
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleClose}>Cancelar</Button>
                    <Button variant="contained" onClick={handleSubmit} disabled={!canSave}>
                        Guardar ruta
                    </Button>
                </DialogActions>
            </Dialog>

            {openLocModal && (
                <LocalizationForm
                    open={openLocModal}
                    handleClose={() => setOpenLocModal(false)}
                    onSubmit={handleCreateLocation}
                />
            )}
        </>
    );
};

export default ManifestAssignRouteForm;